import { Component } from '@theme/component';

/**
 * A custom element that plays a hero video with audio on user interaction.
 * Video sources are lazy-loaded (data-src → src) only when the user clicks play.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} playButton - The play button overlay.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLSourceElement[]} videoSources - The video source elements.
 *
 * @extends Component<Refs>
 */
class HeroVideoComponent extends Component {
  requiredRefs = ['videoElement', 'videoSources'];

  #loaded = false;

  handlePlay = async () => {
    const { videoElement, videoSources } = this.refs;

    if (!this.#loaded) {
      for (const source of videoSources) {
        const src = source.dataset.src;
        if (src) source.setAttribute('src', src);
      }
      videoElement.load();
      this.#loaded = true;
    }

    this.setAttribute('data-playing', 'true');

    try {
      await videoElement.play();
    } catch {
      // Autoplay with audio blocked — mute and retry, then attempt unmute
      videoElement.muted = true;
      await videoElement.play();
    }
  };
}

if (!customElements.get('hero-video-component')) {
  customElements.define('hero-video-component', HeroVideoComponent);
}
